// Shared lead-sheet chrome — the structural marks, signs and chord typesetting
// used by BOTH the chord score and the guitar-diagram grid so the two views are
// laid out identically (iReal Pro parity). Every export is a component, so this
// module stays fast-refresh friendly.

import { useLayoutEffect, useRef } from 'react';
import type { IRealChart, IRealMeasure } from '@/music/ireal/types';
import { useStore } from '@/state/store';
import { parseChordLabel } from '@/music/notation/chordLabel';

const ROMAN: Record<number, string> = { 1: '1', 2: '2', 3: '3' };

/**
 * The printed-chart masthead: style left, tune title centred, composer right.
 * No tempo chip — the transport owns tempo wherever the masthead appears
 * (user ruling July 2026); the model keeps `chart.tempo` for playback. Real
 * Book mode aligns all three across one header band when space allows; CSS
 * stacks the side details below the title on narrow screens.
 */
export function ChartHeader({
  chart,
  edition,
}: {
  chart: IRealChart;
  /** The reading edition printed under the style ("B♭ version"); the C book
   *  prints nothing, so pass null/undefined for a concert sheet. */
  edition?: string | null;
}) {
  const titleRef = useRef<HTMLSpanElement>(null);
  const titleMatch = chart.title.trim().match(/^(.*?)\s*(\([^()]+\))$/);
  const mainTitle = titleMatch?.[1]?.trim() || chart.title.trim() || 'Untitled tune';
  const titleNote = titleMatch?.[2] ?? null;
  const titleLengthClass =
    mainTitle.length > 36
      ? ' is-extra-long'
      : mainTitle.length > 30
        ? ' is-very-long'
        : mainTitle.length > 20
          ? ' is-long'
          : '';

  useLayoutEffect(() => {
    const title = titleRef.current;
    const heading = title?.parentElement;
    if (!title || !heading) return;

    const updateUnderline = () => {
      const range = document.createRange();
      if (typeof range.getClientRects !== 'function') return;
      range.selectNodeContents(title);
      const lineWidths = Array.from(range.getClientRects(), (rect) => rect.width).filter(
        (width) => width > 0,
      );
      if (lineWidths.length === 0) return;
      const width = Math.min(Math.max(...lineWidths) + 8, title.getBoundingClientRect().width - 4);
      heading.style.setProperty('--title-underline-width', `${Math.ceil(width)}px`);
    };

    updateUnderline();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateUnderline);
    observer?.observe(title);
    window.addEventListener('resize', updateUnderline);
    void document.fonts?.ready.then(updateUnderline);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateUnderline);
    };
  }, [mainTitle]);

  return (
    <header className="chart-header" data-testid="chart-header">
      <div className="chart-header-side chart-header-left">
        {chart.style ? <span className="chart-header-style">({chart.style})</span> : null}
        {edition ? (
          <span className="chart-header-edition" data-testid="chart-edition">
            {edition}
          </span>
        ) : null}
      </div>
      <h2 className={`chart-header-title${titleLengthClass}`}>
        <span ref={titleRef} className="chart-header-title-main">
          {mainTitle}
        </span>
        {titleNote ? <span className="chart-header-title-note">{titleNote}</span> : null}
      </h2>
      <div className="chart-header-side chart-header-right">
        {chart.composer ? <span className="chart-header-composer">{chart.composer}</span> : null}
      </div>
    </header>
  );
}

// Crisp inline glyphs — the Unicode musical symbols (𝄋/𝄌/𝄐) lack font support
// in most browsers and render as broken/stacked tofu.
export function CodaGlyph() {
  return (
    <svg
      className="nav-svg"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      aria-hidden
      focusable="false"
    >
      <circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <line x1="12" y1="3" x2="12" y2="21" stroke="currentColor" strokeWidth="1.6" />
      <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}
// Bravura's own segno (SMuFL U+E047, OFL), its outline in font units with the
// y axis flipped: a hand-drawn S at this size read as a squashed scribble.
const SEGNO_PATH =
  'M135 665Q140 665 144 663Q149 660 151 652L153 645Q157 622 173 592Q189 562 226 559Q257 559 276 576Q295 594 295 626Q295 649 287 673Q273 707 233 722Q193 736 153 736Q98 734 52 681Q7 628 4 551Q4 515 20 477Q52 419 122 368Q191 317 205 312Q211 310 211 304Q211 298 205 288Q191 262 127 146Q63 31 54 15Q51 9 51 2Q51 -10 59 -18Q67 -27 79 -27Q87 -27 93 -23Q100 -19 104 -12Q114 5 182 129Q250 252 262 274Q262 274 267 276Q271 279 274 279Q297 276 387 235Q477 194 489 122Q489 93 473 74Q457 56 431 52L428 51Q412 51 401 62Q390 73 390 96V107Q389 136 374 154Q359 172 337 173Q331 173 325 171Q296 165 276 150Q255 135 254 106Q256 59 292 26Q329 -7 375 -8Q395 -8 417 -1Q478 20 514 66Q549 111 550 174Q550 188 548 203Q528 287 454 345Q381 402 363 408Q354 413 350 417Q346 420 346 424Q346 427 348 430Q361 453 430 577Q498 700 508 717Q510 721 511 724Q512 727 512 731Q512 743 504 751Q496 759 484 759Q476 759 470 755Q463 752 459 745Q449 727 379 600Q308 473 294 449Q292 445 290 443Q288 441 285 441Q281 441 275 444Q260 448 188 481Q117 514 89 550Q84 559 80 573Q75 587 75 603Q74 624 86 642Q98 659 129 665ZM415 466Q416 442 432 426Q448 410 472 409Q497 410 513 426Q528 442 529 466Q528 491 513 507Q497 522 472 523Q448 522 432 507Q416 491 415 466ZM140 264Q139 288 124 304Q108 320 83 321Q59 320 43 304Q27 288 26 264Q27 239 43 224Q59 208 83 207Q108 208 124 224Q139 239 140 264Z';
export function SegnoGlyph() {
  return (
    <svg
      className="nav-svg"
      viewBox="-20 -779 594 826"
      width="13"
      height="18"
      aria-hidden
      focusable="false"
    >
      <path d={SEGNO_PATH} transform="scale(1,-1)" fill="currentColor" />
    </svg>
  );
}
export function FermataGlyph() {
  return (
    <svg
      className="nav-svg"
      viewBox="0 0 24 24"
      width="15"
      height="15"
      aria-hidden
      focusable="false"
    >
      <path d="M4 16a8 8 0 0 1 16 0" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="12" cy="15" r="1.6" fill="currentColor" />
    </svg>
  );
}

/** Tall stacked time-signature fraction that leads a bar, as on a real staff. */
export function TimeSig({ sig }: { sig: [number, number] }) {
  const wide = sig[0] >= 10 || sig[1] >= 10;
  return (
    <span className="time-sig" title={`${sig[0]}/${sig[1]}`} data-wide={wide || undefined}>
      <span>{sig[0]}</span>
      <span>{sig[1]}</span>
    </span>
  );
}

/** The simile (one-bar `%`) or double-bar-repeat sign drawn as crisp SVG. */
export function BarRepeatSign({ n }: { n: 1 | 2 }) {
  return (
    <span className="bar-repeat" title={`${n}-bar repeat`} aria-label={`${n} bar repeat`}>
      <svg viewBox="0 0 36 36" width="30" height="30" aria-hidden focusable="false">
        {/* A one-bar repeat (%): a bold slash with a dot in the upper-left and
            lower-right. A two-bar repeat adds a second parallel slash. */}
        <circle cx="13" cy="13" r="2.5" fill="currentColor" />
        <line
          x1="11"
          y1="25"
          x2="25"
          y2="11"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
        {n === 2 && (
          <line
            x1="17"
            y1="25"
            x2="31"
            y2="11"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
          />
        )}
        <circle cx={n === 2 ? 29 : 23} cy="23" r="2.5" fill="currentColor" />
      </svg>
    </span>
  );
}

/** Repeat-sign dots drawn beside a thick {/} barline. */
export function RepeatDots({ side }: { side: 'open' | 'close' }) {
  return <span className={`repeat-dots repeat-dots-${side}`} aria-hidden />;
}

/**
 * A short open volta over the ending's first bar (iReal Pro style): a left tick,
 * a top line spanning that one bar, and the number hanging just under the roof.
 */
export function EndingBracket({ ending }: { ending: number }) {
  return (
    <span className="ending-mark" aria-label={`ending ${ending}`}>
      {ROMAN[ending] ?? ending}.
    </span>
  );
}

/**
 * Section letter (A/B/C…), nav glyphs and fermata that sit above the bar. A
 * letter opening a row (`sectionHead`) shares the bar's top-left corner with
 * its number, so the two are laid out in one row: the letter in the gutter,
 * the number after it, at its usual place unless the letter reaches that far.
 */
export function MeasureMarks({
  measure,
  sectionHead = false,
  barNumber = null,
}: {
  measure: IRealMeasure;
  sectionHead?: boolean;
  barNumber?: number | null;
}) {
  const hasAny = measure.section || measure.segno || measure.coda || measure.fermata;
  if (!hasAny) return null;
  const section = measure.section ? <span className="section-mark">{measure.section}</span> : null;
  return (
    <div className="measure-marks">
      {section && sectionHead ? (
        <span className="section-head">
          <span className="section-slot">{section}</span>
          {barNumber != null && (
            <span className="measure-number" aria-hidden="true">
              {barNumber}
            </span>
          )}
        </span>
      ) : (
        section
      )}
      <span className="measure-marks-spacer" />
      {measure.segno && (
        <span className="nav-mark" title="Segno">
          <SegnoGlyph />
        </span>
      )}
      {measure.coda && (
        <span className="nav-mark" title="Coda">
          <CodaGlyph />
        </span>
      )}
      {measure.fermata && (
        <span className="nav-mark" title="Fermata">
          <FermataGlyph />
        </span>
      )}
    </div>
  );
}

// Beautify accidentals for display only (the underlying symbol text is kept on
// the model; this never changes what the chord *is*).
function prettyAccidentals(s: string): string {
  return s.replace(/b/g, '♭').replace(/#/g, '♯');
}

/**
 * Jazz chord typesetting: a large root (letter + accidental + minor/ø/° mark)
 * with a smaller, raised quality/extensions cluster and a normal-size slash
 * bass — the iReal Pro look. Rendering goes through the SAME `parseChordLabel`
 * the notation staff uses, so a chord reads identically in the chart, guitar
 * and score views; the `chordSymbolStyle` setting picks the dialect.
 */
export function ChordSymbol({
  symbol,
  className,
  reading = false,
}: {
  symbol: string;
  className?: string;
  reading?: boolean;
}) {
  // Black Book is a Real Book style: the bass stacks under the chord.
  const realBook = true;
  const style = useStore((state) => state.chordSymbolStyle);
  const { root, sup, bass, prefix, suffix } = parseChordLabel(prettyAccidentals(symbol), style);

  return (
    <span
      className={`chord-symbol${realBook ? ' realbook-chord-symbol' : ''}${reading ? ' reading-chord-symbol' : ''}${className ? ` ${className}` : ''}`}
      aria-label={prettyAccidentals(symbol)}
    >
      <span className="cs-main" aria-hidden="true">
        {prefix && <span className="cs-root">{prefix}</span>}
        <span className="cs-root">{root}</span>
        {sup && <span className="cs-qual">{sup}</span>}
        {suffix && (!bass || realBook) && <span className="cs-root">{suffix}</span>}
      </span>
      {bass && (
        <span className={`cs-bass${realBook ? ' cs-bass-stacked' : ''}`} aria-hidden="true">
          {bass}
        </span>
      )}
      {suffix && bass && !realBook && (
        <span className="cs-root" aria-hidden="true">
          {suffix}
        </span>
      )}
    </span>
  );
}

/** The analysis lane's mark under a chart chord: italic roman, bold key
 *  prefix where a region starts, and the cadence under-bracket as borders. */
export function ChartRoman({
  mark,
  className,
}: {
  mark: { roman: string; keyPrefix?: string; cell?: string; cellKind?: string };
  className?: string;
}) {
  return (
    <span
      className={`chart-roman${mark.cell ? ` cell-${mark.cell}` : ''}${className ? ` ${className}` : ''}`}
      data-testid="chart-roman"
      data-cell-kind={mark.cellKind}
    >
      {mark.keyPrefix && <b className="chart-roman-key">{mark.keyPrefix} </b>}
      {mark.roman}
    </span>
  );
}
